/**
 * Submission Limits Checking Utility
 *
 * Checks if a submission exceeds free tier limits (10 files or 50MB) and handles
 * navigation to plans page with preview data for upgrade flow.
 *
 * NOTE: These limits only apply to UNAUTHENTICATED users.
 * Authenticated users are limited only by their credit balance.
 *
 * @module utils/jobSubmission/checkSubmissionLimits
 */

import { useNavigate } from '@tanstack/react-router';

// Infer the navigate function type from TanStack Router
type NavigateFunction = ReturnType<typeof useNavigate>;

export interface LimitCheckResult {
  exceedsLimit: boolean;
  reason?: 'count' | 'size';
  attempted?: number;
  limit?: number;
}

export interface LimitPreview {
  name: string;
  size: string;
  preview?: string;
  isImage: boolean;
  actualSize: number;
}

const FREE_TIER_FILE_LIMIT = 10;  // Reduced from 100 to 10
const FREE_TIER_SIZE_LIMIT_MB = 50;  // Reduced from 500MB to 50MB
const PREVIEW_FILE_COUNT = 3;

/**
 * Checks if submission exceeds free tier limits
 *
 * @param files - Array of files to check
 * @returns Object indicating if limits are exceeded and details
 */
export function checkLimits(files: File[]): LimitCheckResult {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const totalSizeMB = totalSize / (1024 * 1024);

  // File count takes precedence
  if (files.length > FREE_TIER_FILE_LIMIT) {
    return {
      exceedsLimit: true,
      reason: 'count',
      attempted: files.length,
      limit: FREE_TIER_FILE_LIMIT
    };
  }

  // Check total size
  if (totalSizeMB > FREE_TIER_SIZE_LIMIT_MB) {
    return {
      exceedsLimit: true,
      reason: 'size',
      attempted: Math.round(totalSizeMB),
      limit: FREE_TIER_SIZE_LIMIT_MB
    };
  }

  return { exceedsLimit: false };
}

/**
 * Creates preview data for first N files for plans page display
 *
 * @param files - Array of files to create previews from
 * @param previewCount - Number of preview files to create (default: 3)
 * @returns Array of preview objects with file metadata
 */
export function createFilePreviews(
  files: File[],
  previewCount: number = PREVIEW_FILE_COUNT
): LimitPreview[] {
  const previewFiles = files.slice(0, previewCount);

  return previewFiles.map((file) => {
    const isImage = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/heic',
      'image/heif'
    ].includes(file.type);

    const isHeicFile =
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif');

    let preview: string | undefined;

    // Skip preview generation for HEIC files since browsers can't display them
    if (isImage && !isHeicFile) {
      try {
        preview = URL.createObjectURL(file);
      } catch (error) {
        console.warn('Failed to generate preview for:', file.name, error);
      }
    }

    return {
      name: file.name,
      size: `${Math.round(file.size / (1024 * 1024))}MB`,
      preview,
      isImage,
      actualSize: file.size
    };
  });
}

/**
 * Gets user-friendly limit message based on limit type
 *
 * @param result - Limit check result
 * @returns User-friendly message explaining the limit
 */
export function getLimitMessage(result: LimitCheckResult): string {
  if (!result.exceedsLimit) {
    return '';
  }

  return `Your batch requires our paid service for processing. Preview your results and choose a plan below.`;
}

/**
 * Handles limit exceeded scenario: creates previews, stores session data, and navigates to plans
 *
 * @param files - Files that exceeded limits
 * @param navigate - TanStack Router navigate function
 * @returns Promise that resolves when navigation is complete
 *
 * @example
 * ```typescript
 * const limitResult = checkLimits(validFiles);
 * if (limitResult.exceedsLimit) {
 *   await handleLimitExceeded(validFiles, navigate, limitResult);
 *   return; // Stop submission flow
 * }
 * ```
 */
export async function handleLimitExceeded(
  files: File[],
  navigate: NavigateFunction,
  limitResult: LimitCheckResult
): Promise<void> {
  try {
    // Create preview data
    const limitPreviews = createFilePreviews(files);

    // Store preview data for plans page
    sessionStorage.setItem(
      'sortingcat_limit_previews',
      JSON.stringify(limitPreviews)
    );

    // Store limit reason
    const limitReason = {
      type: limitResult.reason,
      attempted: limitResult.attempted,
      limit: limitResult.limit,
      message: getLimitMessage(limitResult)
    };

    sessionStorage.setItem(
      'sortingcat_limit_reason',
      JSON.stringify(limitReason)
    );

    // Navigate to plans page using TanStack Router (preserves SPA context)
    setTimeout(() => {
      navigate({
        to: '/plans',
        search: { from: 'limit', imageCount: files.length, selected_plan: undefined }
      });
    }, 1000);
  } catch (err) {
    console.warn('Failed to create submission limit previews:', err);

    // Navigate to plans page even if preview creation fails
    setTimeout(() => {
      navigate({
        to: '/plans',
        search: { from: 'limit', imageCount: files.length, selected_plan: undefined }
      });
    }, 1000);
  }
}
